// Storage as a farmyard, described the way age-of-mcp authors it: a `body`
// helper for the barn plus the shared `foundationSlab`, `scaffold`, `fenceRect`
// and `trough`, walked through its build stages. Ported 1:1 from age-of-mcp's
// storage.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, fenceRect, trough, type Part } from "../../lib";

const WALL = "#a8503a";   // weathered barn red
const ROOF = "#403a3c";   // dark shingle
const TRIM = "#e6dcc2";   // cream frame & braces
const DOOR = "#553c2b";   // dark door wood
const HAY  = "#d8b94e";

const BASE  = 0.14;
const BARNX = -0.45;      // the barn occupies the LEFT half of the 2x2 plot
const BARN_W = 0.96, BARN_D = 1.42;
const FRONT = BARN_D / 2;   // z of the barn's door face

// The barn body — `h` tiles of wall on the foundation, on the left of the plot.
function body(h: number): Part {
  return { geometry: new THREE.BoxGeometry(BARN_W, h, BARN_D), color: WALL, position: [BARNX, BASE + h / 2, 0] };
}

// The build stages of the storage yard: foundation, barn body with scaffolding,
// taller body, and the finished barn with gable roof, doors, hayloft and the
// fenced paddock with trough and hay.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), body(0.4), ...scaffold(1.0, 0.85)];
  if (s === 2) return [foundationSlab(1.94), body(0.78), ...scaffold(1.0, 1.5)];

  const wallH = 0.92;
  const top   = BASE + wallH;
  const rise  = 0.56;
  const halfD = BARN_D / 2 + 0.1;
  const slope = Math.hypot(halfD, rise);
  const ang   = Math.atan2(rise, halfD);
  const dz    = FRONT + 0.03;

  return [
    foundationSlab(1.94),
    body(wallH),
    // Gable roof over the barn — two planes to a pale ridge.
    { geometry: new THREE.BoxGeometry(BARN_W + 0.12, 0.08, slope), color: ROOF,
      position: [BARNX, top + rise / 2,  halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(BARN_W + 0.12, 0.08, slope), color: ROOF,
      position: [BARNX, top + rise / 2, -halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(BARN_W + 0.16, 0.08, 0.08), color: TRIM,
      position: [BARNX, top + rise, 0] },
    // Big double doors on the front.
    { geometry: new THREE.BoxGeometry(0.32, 0.72, 0.05), color: DOOR, position: [BARNX - 0.18, BASE + 0.36, dz] },
    { geometry: new THREE.BoxGeometry(0.32, 0.72, 0.05), color: DOOR, position: [BARNX + 0.18, BASE + 0.36, dz] },
    { geometry: new THREE.BoxGeometry(0.82, 0.07, 0.07), color: TRIM, position: [BARNX, BASE + 0.76, dz] },
    { geometry: new THREE.BoxGeometry(0.07, 0.8, 0.07), color: TRIM, position: [BARNX - 0.38, BASE + 0.4, dz] },
    { geometry: new THREE.BoxGeometry(0.07, 0.8, 0.07), color: TRIM, position: [BARNX + 0.38, BASE + 0.4, dz] },
    // Hayloft hatch high on the gable.
    { geometry: new THREE.BoxGeometry(0.34, 0.26, 0.05), color: DOOR, position: [BARNX, top - 0.06, dz - 0.02] },
    { geometry: new THREE.BoxGeometry(0.4, 0.06, 0.06), color: TRIM, position: [BARNX, top + 0.1, dz - 0.02] },
    // The PADDOCK on the right: a rail fence with a trough and a hay stack.
    ...fenceRect(0.12, -0.86, 0.92, 0.86),
    ...trough(0.5, -0.2),
    { geometry: new THREE.BoxGeometry(0.36, 0.22, 0.26), color: HAY, position: [0.62, BASE + 0.11, 0.5] },
    { geometry: new THREE.BoxGeometry(0.36, 0.22, 0.26), color: HAY, position: [0.62, BASE + 0.33, 0.5] },
  ];
}

export const storage = defineModel({
  id: "storage",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Storage",
  tags: ["building","storage","low-poly","age-of-mcp","classic"],
  summary: "Low-poly storage.",
  description: "A walled yard of stacked crates under slanted plank roofs, holding far more goods than a chest.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
